import {
  type Interest,
  type EducationLevel,
  type Gender,
  type Profession,
  type Image,
  type ReduxUser,
  DefaultImage,
} from 'models';
import { countries } from 'old/utils/countries';
import { USstates } from 'old/utils/us-states';
import { useTranslation } from 'react-i18next';
import { uploadImage } from 'common/queries';
import variables from 'common/styles/variables.json';

import { images, timezones } from 'common/utils';

import {
  Col,
  Form,
  PhoneInput,
  Row,
  SelectInput,
  TextInput,
  TextAreaInput,
  Item,
  BirthDateInput,
  Button,
  Fieldset,
  type ImageUploadedData,
  type BirthDateType,
  ImageInput,
  getBirthDateTypeValue,
  transformImageForInit,
} from 'ui';

export type ProfileFormValues = {
  profilePhoto?: ImageUploadedData<DefaultImage | Image>;
  firstName: string;
  middleName?: string;
  lastName: string;
  email: string;
  phone: string;
  birthDate?: BirthDateType;
  timeZone: string;
  gender?: string;
  country?: string;
  state?: string;
  city?: string;
  zip?: string;
  street?: string;
  apt?: string;
  educationLevel?: string;
  company?: string;
  profession?: string;
  biography?: string;
  interests?: string[];
};

const getInitialValues = (user: ReduxUser): Partial<ProfileFormValues> => ({
  firstName: user.first_name,
  middleName: user.middle_name,
  lastName: user.last_name,
  email: user.email,
  phone: user.phone,
  timeZone: user.time_zone,
  birthDate: getBirthDateTypeValue(user.birthdate),
  profilePhoto: user.profile_photo
    ? transformImageForInit(user.profile_photo)
    : transformImageForInit(new DefaultImage({ url: images.default.userProfilePhoto })),
  educationLevel: user.education_level?.id,
  gender: user.gender?.id,
  profession: user.profession?.id,
  interests: user.interests?.map((interest) => interest.id),
  company: user.company,
  biography: user.biography,
  country: user.country,
  state: user.state,
  city: user.city,
  zip: user.zip,
  street: user.street,
  apt: user.apt,
});

type P = {
  user: ReduxUser;
  onSubmit: (values: ProfileFormValues) => void;
  genders: Gender[];
  professions: Profession[];
  educationLevels: EducationLevel[];
  interests: Interest[];
  loading: boolean;
};

const ProfileForm = ({ user, onSubmit, genders, professions, educationLevels, interests, loading }: P) => {
  const { t } = useTranslation();
  const [form] = Form.useForm<ProfileFormValues>();

  const onFinish = (data: ProfileFormValues) => {
    onSubmit(data);
  };

  return (
    <Form<ProfileFormValues>
      onFinish={onFinish}
      form={form}
      initialValues={getInitialValues(user)}
      className="profile-form"
    >
      <Row>
        <Col>
          <ImageInput uploadFile={uploadImage} item={{ name: 'profilePhoto' }} type="round" size="large" />
        </Col>

        <Col item={{ span: 24 }}>
          <Fieldset legend={{ title: 'Full name' }}>
            <Row item={{ gutter: [variables.spaceXs.value, variables.spaceXs.value] }}>
              <Col item={{ span: 8 }}>
                <TextInput
                  item={{
                    name: 'firstName',
                    label: 'First Name',
                    rules: [
                      {
                        required: true,
                      },
                    ],
                  }}
                />
              </Col>
              <Col item={{ span: 8 }}>
                <TextInput
                  item={{
                    name: 'middleName',
                    label: 'Middle Name',
                  }}
                />
              </Col>
              <Col item={{ span: 8 }}>
                <TextInput
                  item={{
                    name: 'lastName',
                    label: 'Last Name',
                    rules: [
                      {
                        required: true,
                      },
                    ],
                  }}
                />
              </Col>
            </Row>
          </Fieldset>
        </Col>

        <Col item={{ span: 24 }}>
          <TextInput
            item={{
              name: 'email',
              label: 'Email',
              rules: [
                {
                  required: true,
                },
              ],
            }}
          />
        </Col>

        <Col item={{ span: 24 }}>
          <PhoneInput
            item={{
              name: 'phone',
              label: 'Your phone number',
              rules: [
                {
                  required: true,
                },
              ],
            }}
          />
        </Col>

        <Col item={{ span: 24 }}>
          <BirthDateInput
            item={{
              label: 'Date of birth',
              name: 'birthDate',
            }}
          />
        </Col>

        <Col item={{ span: 24 }}>
          <SelectInput
            item={{
              name: 'timeZone',
              label: 'Time Zone',
              rules: [
                {
                  required: true,
                },
              ],
            }}
            input={{
              showSearch: true,
              options: timezones,
              getOptionLabel: (option) => option.label,
              getOptionValue: (option) => option.value,
            }}
          />
        </Col>

        <Col item={{ span: 24 }}>
          <SelectInput
            item={{
              name: 'gender',
              label: 'Gender',
            }}
            input={{
              options: genders,
              getOptionLabel: (option) => option.name,
              getOptionValue: (option) => option.id,
            }}
          />
        </Col>

        <Col item={{ span: 24 }}>
          <Fieldset legend={{ title: 'Address' }}>
            <Row>
              <Col item={{ span: 24 }}>
                <SelectInput
                  item={{
                    name: 'country',
                    label: 'Country',
                  }}
                  input={{
                    options: countries,
                    getOptionLabel: (option) => option.name,
                    getOptionValue: (option) => option.code,
                  }}
                />
              </Col>

              <Col item={{ span: 24 }}>
                <Item dependencies={['country']} noStyle>
                  {({ getFieldValue }) => {
                    return (
                      <>
                        {getFieldValue('country') === 'US' ? (
                          <SelectInput
                            item={{
                              name: 'state',
                              label: 'State',
                            }}
                            input={{
                              options: USstates,
                              getOptionLabel: (option) => option.name,
                              getOptionValue: (option) => option.code,
                            }}
                          />
                        ) : null}
                      </>
                    );
                  }}
                </Item>
              </Col>

              <Col item={{ span: 24 }}>
                <Row item={{ gutter: [variables.spaceXs.value, 0] }}>
                  <Col item={{ xs: { span: 24 }, lg: { span: 18 } }}>
                    <TextInput
                      item={{
                        name: 'city',
                        label: 'City',
                      }}
                    />
                  </Col>
                  <Col item={{ xs: { span: 24 }, lg: { span: 6 } }}>
                    <TextInput
                      item={{
                        name: 'zip',
                        label: 'ZIP',
                      }}
                    />
                  </Col>
                </Row>
              </Col>

              <Col item={{ span: 24 }}>
                <Row item={{ gutter: [variables.spaceXs.value, variables.spaceXs.value] }}>
                  <Col item={{ xs: { span: 24 }, lg: { span: 18 } }}>
                    <TextInput
                      item={{
                        name: 'street',
                        label: 'Street',
                      }}
                    />
                  </Col>
                  <Col item={{ xs: { span: 24 }, lg: { span: 6 } }}>
                    <TextInput
                      item={{
                        name: 'apt',
                        label: 'Apartment',
                      }}
                    />
                  </Col>
                </Row>
              </Col>
            </Row>
          </Fieldset>
        </Col>

        <Col item={{ span: 24 }}>
          <SelectInput
            item={{
              name: 'educationLevel',
              label: 'Education Level',
            }}
            input={{
              options: educationLevels,
              getOptionLabel: (option) => option.name,
              getOptionValue: (option) => option.id,
            }}
          />
        </Col>

        <Col item={{ span: 24 }}>
          <TextInput
            item={{
              name: 'company',
              label: 'Name of company/school',
            }}
          />
        </Col>

        <Col item={{ span: 24 }}>
          <SelectInput
            item={{
              name: 'profession',
              label: 'Profession',
            }}
            input={{
              options: professions,
              getOptionLabel: (option) => t(option.name),
              getOptionValue: (option) => option.id,
            }}
          />
        </Col>

        <Col item={{ span: 24 }}>
          <TextAreaInput
            item={{
              name: 'biography',
              label: 'Biography',
            }}
          />
        </Col>

        <Col item={{ span: 24 }}>
          <SelectInput
            item={{
              name: 'interests',
              label: 'Interests (you can choose multiple)',
              // TODO after update on ant design 5 in #57416 has Select input new propery maxCount
              rules: [
                {
                  async validator(_, value) {
                    if (value.length > 3) {
                      throw 'Error: You can choose only 3 interests';
                    }
                  },
                },
              ],
            }}
            input={{
              options: interests,
              mode: 'multiple',
              showSearch: true,
              getOptionLabel: (option) => t(option.name),
              getOptionValue: (option) => option.id,
            }}
          />
        </Col>

        <Col item={{ span: 24 }}>
          <Button type="primary" htmlType="submit" loading={loading} size="large">
            Save
          </Button>
        </Col>
      </Row>
    </Form>
  );
};

export default ProfileForm;
